import {
  RANGE_KEY,
  STAGE_ORDER,
  UNKNOWN_PARENT,
  displayCompanyName,
  type FactKind,
  formatWindow,
  type CompletionWindow,
  type SituationDisposition,
  type Stage,
} from "@mosaic/shared";

/**
 * Display vocabulary, kept in one module so the radar row, the group header and the
 * eyebrow in the reading pane cannot describe the same stage three different ways.
 */

export const STAGE_LABEL: Record<Stage, string> = {
  announced: "Announced",
  form10_filed: "Form 10 filed",
  terms_set: "Terms set",
  when_issued: "When-issued",
  completed: "Completed",
  abandoned: "Abandoned",
};

/** One colour per stage, in pipeline order — the `--s1…--s5` tokens from the mockup. */
export function stageColorVar(stage: Stage): string {
  if (stage === "abandoned") return "var(--ink-3)";
  const index = STAGE_ORDER.indexOf(stage);
  return `var(--s${index >= 0 ? index + 1 : 1})`;
}

/**
 * The owner's judgement as a state, in the words the UI uses for it.
 *
 * `unreviewed` has a label because the situation page names it when returning a
 * situation; the radar never draws a chip for it, since a chip on every row marks
 * nothing.
 */
export const DISPOSITION_LABEL: Record<SituationDisposition, string> = {
  unreviewed: "Unreviewed",
  not_a_situation: "Not a separation",
  not_interesting: "Dismissed",
  watching: "Watching",
};

/**
 * The same judgements as actions, which is what a button is.
 *
 * Two maps rather than one: a button says what pressing it does and a chip says what was
 * decided. Shared, a control would read "Watching" and the record of a past decision would
 * read as an instruction. Every pair differs only in tense, so one word never names two
 * sets on one screen.
 */
export const DISPOSITION_ACTION: Record<SituationDisposition, string> = {
  unreviewed: "Return to radar",
  not_a_situation: "Not a separation",
  not_interesting: "Dismiss",
  watching: "Watch",
};

/**
 * The judgements offered as buttons, in the order they are offered.
 *
 * `unreviewed` is absent: every situation starts there, so a button for it is a control for
 * the state you are already in. Returning to the radar is a separate control that appears
 * once there is a judgement to undo.
 *
 * Watch first because it is the one affirmative choice; the two dismissals after it, the
 * narrower one last.
 */
export const DISPOSITION_CHOICES: readonly SituationDisposition[] = [
  "watching",
  "not_interesting",
  "not_a_situation",
] as const;

/**
 * What each judgement means for the pipeline, said where the owner is choosing one.
 *
 * `not_a_situation` stops reconciliation matching new filings against this situation and
 * `not_interesting` does not, so the two dismissals must not read as two shades of "no".
 */
export const DISPOSITION_BLURB: Record<SituationDisposition, string> = {
  unreviewed: "Nothing decided. Sits on the radar.",
  not_a_situation: "Future filings will open a new situation.",
  not_interesting:
    "Remove from the radar. Future relevant filings will still add to this situation.",
  watching: "Keep it on the radar.",
};

/** Muted for a dismissal, positive for a hold. Never a score. */
export function dispositionClass(disposition: SituationDisposition): string {
  return disposition === "watching" ? "good" : "muted";
}

/** Groups shown on the radar, in the order the index column stacks them. */
export const RADAR_STAGES: readonly Stage[] = [
  "when_issued",
  "terms_set",
  "form10_filed",
  "announced",
  "completed",
  "abandoned",
] as const;

/**
 * A distribution ratio for display, as `numerator : denominator`.
 *
 * @param r The ratio, or null when none is known.
 * @returns The formatted ratio, or `—` when there is none.
 */
export function ratio(r: { numerator: number; denominator: number } | null): string {
  return r ? `${r.numerator} : ${r.denominator}` : "—";
}

/** `2025-02-21` → `02-21`. The year is almost always implied by the row above it. */
export function shortDate(iso: string | null): string {
  return iso ? iso.slice(5) : "—";
}

/**
 * The countdown the radar row leads with. Server-computed so every client agrees;
 * this only decides how to say it.
 *
 * Falls back to the window an announcement stated ("H2 2026"), because a year can pass
 * between an announcement and the Form 10 and lead time is the radar's value in that gap.
 *
 * A window never gets the near accent and never reads like a countdown: `isNear` sees only
 * `days`. "in 6d" is a date somebody committed to; "H2 2026" is a period somebody
 * gestured at, and rendering them alike would claim a precision the filing does not.
 */
export function whenLabel(
  days: number | null,
  stage: Stage,
  expected: CompletionWindow | null = null,
): string {
  if (days === null) {
    if (stage === "completed") return "done";
    return expected ? formatWindow(expected) : "—";
  }
  if (days === 0) return "today";
  if (days > 0) return `in ${days}d`;
  return `+${Math.abs(days)}d`;
}

/** Inside a fortnight is worth the accent colour; beyond it is just information. */
export function isNear(days: number | null): boolean {
  return days !== null && days >= 0 && days <= 14;
}

/** Display labels for each tax treatment value. */
export const TAX_LABEL: Record<string, string> = {
  section_355: "§355",
  taxable: "Taxable",
  rmt: "RMT",
  unknown: "—",
};

/** `debt_pushed_down` → `DEBT PUSHED DOWN`, for the flag chips. */
export function flagChipLabel(kind: string): string {
  return kind.replace(/_/g, " ").toUpperCase();
}

/**
 * The CSS modifier class for a flag severity chip.
 *
 * @param severity The flag's severity.
 * @returns `crit` for `critical`, `warn` for `watch`, otherwise `info`.
 */
export function severityClass(severity: string): string {
  return severity === "critical" ? "crit" : severity === "watch" ? "warn" : "info";
}

/** A company's display handle: the ticker if it has one, else the name. */
export function companyHandle(c: { name: string; ticker: string | null }): string {
  return c.ticker ?? c.name;
}

/**
 * A company named for a table: `Western Digital [WDC]`.
 *
 * Distinct from `companyHandle`, which answers ticker-or-name for the radar's identity
 * line, where the row's title already carries the SpinCo's name. A table row has no title
 * above it, and a ticker alone is unreadable to anyone who does not know it while a name
 * alone drops the token a reader would search for.
 *
 * The corporate suffix comes off because a dense column has little width and `Corp`, `Inc`
 * and `Ltd` distinguish nothing in it. `displayCompanyName` trims from the end only and
 * never changes case, so `QXO` stays readable.
 *
 * A side with no ticker renders as the name alone rather than an empty bracket, and the
 * unknown-parent placeholder passes through as the sentence it already is.
 */
export function denseCompanyLabel(c: { name: string; ticker: string | null }): string {
  const name = displayCompanyName(c.name);
  return c.ticker ? `${name} [${c.ticker}]` : name;
}

/**
 * The identity line under a radar row's title: `RHLD ← CMPO`.
 *
 * The row's title is already the SpinCo's name, so only the side that adds something is
 * shown; before a ticker is known, `companyHandle` would repeat the name.
 *
 * **A parent is known when it has a name, not when it has a CIK.** A `10-12B` is filed by
 * the SpinCo, so `parent_cik` stays null until an exact registry match fills it while the
 * parent's name is already read from the document. Whether the name is matched to an EDGAR
 * company is the deck's to say, not this line's.
 */
export function identityLine(s: {
  spinco: { name: string; ticker: string | null };
  parent: { cik: string | null; name: string; ticker: string | null };
}): string {
  const parent = s.parent.name !== UNKNOWN_PARENT ? companyHandle(s.parent) : null;
  // Named but tickerless means the title already carries it.
  const spinco = s.spinco.ticker;

  if (spinco && parent) return `${spinco} ← ${parent}`;
  if (spinco) return `${spinco} · parent unknown`;
  if (parent) return `from ${parent}`;
  return "parent unknown";
}

/**
 * A money value at a readable magnitude: "none" for zero, a `bn` or `m` suffix from a
 * million up, and grouped digits below that. Every figure is scaled on the decimal string.
 *
 * @param value A fact value; anything without a string `amount` is not money.
 * @returns The display string, or null when `value` is not money. An amount that is not a
 *   plain decimal string is printed as stored.
 */
export function formatMoney(value: unknown): string | null {
  if (typeof value !== "object" || value === null) return null;
  const money = value as { amount?: unknown; currency?: unknown };
  if (typeof money.amount !== "string") return null;

  const amount = money.amount;
  const currency = typeof money.currency === "string" ? money.currency : "USD";
  const symbol = currency === "USD" ? "$" : `${currency} `;
  if (!/^-?\d+(\.\d+)?$/.test(amount)) return `${symbol}${amount}`;
  if (!/[1-9]/.test(amount)) return "none";

  // Digits before the point: 10 or more is at least a billion, 7 or more a million.
  const [whole = "", frac = ""] = amount.replace(/^-/, "").split(".");
  const digits = whole.replace(/^0+/, "").length;
  // A suffixed figure is printed ungrouped.
  const suffixed = (power: number, places: number, suffix: string) =>
    `${symbol}${scaleDecimal(amount, power, places).replace(/,/g, "")}${suffix}`;
  if (digits >= 10) return suffixed(9, digits >= 11 ? 0 : 1, "bn");
  if (digits >= 7) return suffixed(6, digits >= 8 ? 0 : 1, "m");
  const places = Math.min(frac.replace(/0+$/, "").length, 3);
  const scaled = scaleDecimal(amount, 0, places);
  return `${symbol}${places > 0 ? scaled.replace(/\.?0+$/, "") : scaled}`;
}

/**
 * A stated range, or null. Shape-only, like the server's `isStatedRange`, and keyed by
 * the shared `RANGE_KEY` so a rename cannot leave the grid rendering raw JSON.
 *
 * @internal
 */
function asStatedRange(value: unknown): { min: unknown; max: unknown } | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const r = (value as Record<string, unknown>)[RANGE_KEY];
  if (typeof r !== "object" || r === null) return null;
  const { min, max } = r as { min?: unknown; max?: unknown };
  return min === undefined || max === undefined ? null : { min, max };
}

/**
 * Renders a fact's value for the terms grid, per its kind.
 *
 * A stated range renders as both ends with an en dash ("6% – 12%", "1 : 1 – 3 : 1"), never
 * one end or a midpoint, which is a number no filing contains. It is handled before the
 * switch because it is a property of the value, not of the kind.
 */
export function factDisplay(kind: string, value: unknown): string {
  const span = asStatedRange(value);
  if (span) return `${factDisplay(kind, span.min)} – ${factDisplay(kind, span.max)}`;

  switch (kind) {
    case "distribution_ratio":
      return typeof value === "object" && value !== null
        ? ratio(value as { numerator: number; denominator: number })
        : "—";
    case "spinco_debt":
    case "segment_revenue":
      return formatMoney(value) ?? "—";
    case "tax_treatment":
      return TAX_LABEL[String(value)] ?? String(value);
    case "parent_retained_stake_pct":
      return typeof value === "number" ? (value === 0 ? "none" : `${value}%`) : "—";
    case "tsa_term_months":
      return typeof value === "number" ? `${value} mo` : "—";
    default:
      return typeof value === "string" ? value : JSON.stringify(value);
  }
}

/**
 * Scales and rounds a decimal string without ever constructing a double.
 *
 * Divides by 10^`powerOfTen`, rounds half-up to `decimals` places and groups the
 * thousands, all on the digit string, so the digits filed are the digits shown. A value
 * that is not a plain decimal string is returned unchanged.
 */
export function scaleDecimal(value: string, powerOfTen: number, decimals: number): string {
  if (!/^-?\d+(\.\d+)?$/.test(value)) return value;
  const negative = value.startsWith("-");
  const body = negative ? value.slice(1) : value;
  const [intPart = "0", fracPart = ""] = body.split(".");

  let digits = intPart + fracPart;
  // Where the decimal point sits within `digits` once divided by 10^powerOfTen.
  let point = intPart.length - powerOfTen;
  if (point <= 0) {
    const pad = 1 - point;
    digits = "0".repeat(pad) + digits;
    point += pad;
  }
  if (point > digits.length) digits += "0".repeat(point - digits.length);

  const cut = point + decimals;
  if (cut < digits.length) {
    const roundUp = Number(digits[cut]) >= 5;
    const kept = digits.slice(0, cut).split("");
    if (roundUp) {
      let i = kept.length - 1;
      let carry = true;
      while (i >= 0 && carry) {
        const next = Number(kept[i]) + 1;
        kept[i] = String(next % 10);
        carry = next === 10;
        i--;
      }
      // 9.95 rounding to 10.0 gains a digit, which moves the point rather than
      // overwriting the leading one.
      if (carry) {
        kept.unshift("1");
        point += 1;
      }
    }
    digits = kept.join("");
  } else {
    digits += "0".repeat(cut - digits.length);
  }

  const whole = (digits.slice(0, point) || "0").replace(/^0+(?=\d)/, "");
  const frac = digits.slice(point);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const out = decimals > 0 ? `${grouped}.${frac}` : grouped;
  // "-0.0" is zero, and showing it as negative invents a distinction.
  return negative && /[1-9]/.test(digits) ? `-${out}` : out;
}

/**
 * An XBRL value at a readable magnitude, with the scale named rather than implied: an
 * unlabelled scaled column cannot say which figure is a rounding artefact.
 */
export function formatXbrlValue(value: string, unit: string): string {
  if (unit === "USD/shares") return `$${scaleDecimal(value, 0, 2)}`;
  if (unit === "shares") {
    const digits = value.replace(/^-|\..*$/g, "").length;
    return digits > 6 ? `${scaleDecimal(value, 6, 1)}m` : scaleDecimal(value, 0, 0);
  }
  if (unit !== "USD") return `${scaleDecimal(value, 0, 0)} ${unit}`;

  const digits = value.replace(/^-|\..*$/g, "").length;
  if (digits > 9) return `$${scaleDecimal(value, 9, 2)}bn`;
  if (digits > 6) return `$${scaleDecimal(value, 6, 1)}m`;
  return `$${scaleDecimal(value, 0, 0)}`;
}

/**
 * Bytes at a readable scale, in the units Postgres itself uses.
 *
 * Base 1024 with `kB`/`MB`/`GB` labels is what `pg_size_pretty()` prints. Not corrected to
 * `KiB`, so the owner can check the figure in psql and see the same one.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const units = ["kB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/**
 * A span in the largest two units that carry information: `4h 51m`, `18m 20s`, `42s`.
 *
 * Never a bare decimal of hours: this is a record of something that happened, and the
 * minutes are what a reader checks.
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const whole = Math.round(seconds);
  if (whole < 60) return `${whole}s`;
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${whole % 60}s`;
}

const CHICAGO = "America/Chicago";

const SHORT_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: CHICAGO,
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** The same instant spelled out, zone included, for the `title` a reader can hover. */
const FULL = new Intl.DateTimeFormat("en-US", {
  timeZone: CHICAGO,
  dateStyle: "medium",
  timeStyle: "long",
});

/**
 * A timestamp in the owner's own timezone, which is Chicago, or null for an unparseable
 * string.
 *
 * Everything is stored and served as UTC and converted here, at the edge, once. `Intl`
 * rather than an offset, so the platform's tz database handles daylight saving. The short
 * form is assembled from parts because `toLocaleString` changes width with the hour, and a
 * list scanned vertically needs its columns to line up.
 */
export function chicagoTime(iso: string): { short: string; full: string } | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;

  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    SHORT_PARTS.formatToParts(at).find((p) => p.type === type)?.value ?? "";
  // `MM-DD HH:mm`, fixed width, so a column of them reads as a column.
  const short = `${part("month")}-${part("day")} ${part("hour")}:${part("minute")}`;

  return { short, full: FULL.format(at) };
}

/**
 * A fact kind's name in the owner's words, for every kind the schema declares.
 *
 * One map rather than a label beside each renderer: `TermsGrid` decides which kinds the
 * grid shows, and the filing delta renders kinds the grid leaves out.
 *
 * Keyed by `FactKind` so a kind added to the schema fails to compile here rather than
 * rendering as its own identifier on screen.
 */
export const FACT_LABEL: Record<FactKind, string> = {
  distribution_ratio: "Ratio",
  record_date: "Record date",
  distribution_date: "Distribution",
  when_issued_start: "When-issued",
  spinco_debt: "SpinCo debt",
  parent_retained_stake_pct: "Retained stake",
  tax_treatment: "Tax",
  tsa_term_months: "TSA term",
  spinco_ticker: "Ticker",
  spinco_exchange: "Exchange",
  employee_count: "Employees",
  segment_revenue: "Segment revenue",
  parent_name: "Parent",
  spinco_name: "SpinCo",
  parent_ticker: "Parent ticker",
  expected_completion: "Expected",
  parent_business: "Parent business",
  spinco_business: "SpinCo business",
  spinco_peers: "Named competitors",
};


/**
 * The countdown in words, for a byline that has room for them.
 *
 * Distinct from `whenLabel`, which is terse because it sits in a table cell under a header
 * that supplies the subject. A byline is read as a sentence.
 *
 * Returns null rather than a dash when there is nothing to count to: the byline drops the
 * item, where a table cell has to render something to keep its column.
 */
export function countdownLabel(days: number | null, stage: Stage): string | null {
  if (days === null) return stage === "completed" ? "distributed" : null;
  if (days === 0) return "distributing today";
  const n = Math.abs(days);
  const unit = n === 1 ? "day" : "days";
  return days > 0 ? `in ${n} ${unit}` : `${n} ${unit} ago`;
}
